import { useCallback, useEffect, useRef, useState } from 'react'
import { HttpError } from './http.ts'
import { readJson, writeJson } from './storage.ts'

export type Resource<T> =
  | { state: 'loading' }
  | { state: 'ok'; data: T }
  | { state: 'error'; message: string }

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

export interface ResourceCache<T> {
  /** null = necachovat (napr. jeste neni znama skupina). */
  key: string | null
  parse(data: unknown): T | null
}

function fromCache<T>(cache: ResourceCache<T> | undefined): Resource<T> | null {
  if (!cache?.key) return null
  const data = cache.parse(readJson(cache.key))
  return data === null ? null : { state: 'ok', data }
}

interface Registered {
  key(): string | null
  reload(): void
  sync(): void
}

/**
 * Otevrena data vsech obrazovek. Stejna data byvaji nactena na vic mistech
 * naraz (obchody v bocnim panelu i na strance Skupina) — zmena na jednom
 * miste se musi propsat i do ostatnich, jinak ukazuji stary stav az do obnoveni.
 */
const registry = new Set<Registered>()

function notifySameKey(key: string, source: Registered): void {
  // Az po vykresleni: ze setState jedne komponenty se nesmi menit stav jine.
  queueMicrotask(() => {
    for (const entry of registry) if (entry !== source && entry.key() === key) entry.sync()
  })
}

/**
 * Uprava dat v cache bez site (offline dokonceni nakupu) — otevrene obrazovky
 * se stejnym klicem se hned prekresli.
 */
export function updateCachedResource(key: string, update: (prev: unknown) => unknown): void {
  const prev = readJson(key)
  if (prev === null) return
  writeJson(key, update(prev))
  for (const entry of registry) if (entry.key() === key) entry.sync()
}

/** Znovu nacte vsechna otevrena data, jejichz klic zacina prefixem (treba vsechny seznamy obchodu skupiny). */
export function invalidateResources(prefix: string): void {
  for (const entry of registry) if (entry.key()?.startsWith(prefix)) entry.reload()
}

/**
 * Nacte data a nabidne `setData` pro optimisticke upravy po uspesnem zapisu,
 * aby se po kazdem kliknuti nemusel stahovat cely seznam znovu.
 *
 * S `cache` se posledni data drzi v localStorage: obrazovka se ukaze hned
 * a bez signalu zustane na posledni znamem stavu misto chyby.
 */
export function useResource<T>(load: () => Promise<T>, deps: readonly unknown[], cache?: ResourceCache<T>) {
  const [res, setRes] = useState<Resource<T>>(() => fromCache(cache) ?? { state: 'loading' })
  const [offline, setOffline] = useState(false)
  const [tick, setTick] = useState(0)
  const cacheRef = useRef(cache)
  cacheRef.current = cache
  const keyRef = useRef(cache?.key ?? null)
  const [registered] = useState<Registered>(() => ({
    key: () => cacheRef.current?.key ?? null,
    reload: () => setTick((t) => t + 1),
    sync: () => {
      const cached = fromCache(cacheRef.current)
      if (cached) setRes(cached)
    },
  }))

  useEffect(() => {
    registry.add(registered)
    return () => {
      registry.delete(registered)
    }
  }, [registered])

  useEffect(() => {
    let live = true
    const key = cacheRef.current?.key ?? null
    if (key !== keyRef.current) {
      // Jina skupina nebo jiny nakup — stara data sem nepatri.
      keyRef.current = key
      setRes(fromCache(cacheRef.current) ?? { state: 'loading' })
    } else {
      // Pri znovunacteni nechat stara data na obrazovce, ne blikat "Nacitam".
      setRes((prev) => (prev.state === 'ok' ? prev : { state: 'loading' }))
    }
    load()
      .then((data) => {
        if (!live) return
        setRes({ state: 'ok', data })
        setOffline(false)
        if (key) {
          writeJson(key, data)
          notifySameKey(key, registered)
        }
      })
      .catch((err: unknown) => {
        if (!live) return
        const isOffline = err instanceof HttpError && err.isOffline
        setOffline(isOffline)
        setRes((prev) => (isOffline && prev.state === 'ok' ? prev : { state: 'error', message: errorMessage(err) }))
      })
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  const reload = useCallback(() => setTick((t) => t + 1), [])

  const setData = useCallback(
    (update: (prev: T) => T) => {
      setRes((prev) => {
        if (prev.state !== 'ok') return prev
        const data = update(prev.data)
        const key = cacheRef.current?.key
        if (key) {
          writeJson(key, data)
          notifySameKey(key, registered)
        }
        return { state: 'ok', data }
      })
    },
    [registered],
  )

  return { res, reload, setData, offline }
}
